import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faChevronDown,
    faChevronRight,
    faSpinner,
} from "@fortawesome/sharp-light-svg-icons";
import * as d3 from "d3";
import { useAtom } from "jotai";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { useResizeObserver } from "../../../../../ui/Plot/hooks";
import { selectionContextAtom } from "../../state";
import { computePlotTicks, TIMELINE_MARGINS } from "./plot";

const PreviewPlot = ({
    dateRange,
    previewPoints,
}: {
    dateRange?: {
        start: Date;
        end: Date;
    };
    previewPoints: Date[];
}) => {
    const containerRef = useRef(null);
    const svgRef = useRef(null);
    const dimensions = useResizeObserver(containerRef);
    const [selectionContext] = useAtom(selectionContextAtom);

    useEffect(() => {
        if (dimensions.width === 0 || dimensions.height === 0) return;
        if (!dateRange || !dateRange.start || !dateRange.end) return;

        // Clear previous SVG content
        d3.select(svgRef.current).selectAll("*").remove();

        // Set up dimensions
        const margin = TIMELINE_MARGINS;
        const width = dimensions.width - margin.left - margin.right;
        const height = dimensions.height - margin.top - margin.bottom;

        // Create SVG
        const svg = d3
            .select(svgRef.current)
            .attr("viewBox", `0 0 ${dimensions.width} ${dimensions.height}`)
            .attr("width", dimensions.width)
            .attr("height", dimensions.height);

        // Define time range
        const startDate = dateRange.start;
        const endDate = dateRange.end;

        const x = d3.scaleUtc().domain([startDate, endDate]).range([0, width]);

        // Create the chart group
        const chart = svg
            .append("g")
            .attr("transform", `translate(${margin.left},${margin.top})`);

        // Compute ticks
        const { tickValues } = computePlotTicks({
            startDate,
            endDate,
            plotWidth: width,
        });

        // Add grid lines for x-axis
        chart
            .append("g")
            .attr("class", "grid")
            .attr("transform", `translate(0,${height - margin.bottom})`)
            .call(
                d3
                    .axisBottom(x)
                    .tickValues(tickValues)
                    .tickSize(-height + margin.top + margin.bottom),
            )
            .call((g) => g.select(".domain").remove())
            .selectAll("line")
            .style("stroke", "rgb(200,200,200)")
            .style("stroke-dasharray", "4,4");

        chart
            .selectAll(".preview-point")
            .data(previewPoints)
            .join("circle")
            .attr("class", "preview-point")
            .attr("cx", (d: Date) => x(d))
            .attr("cy", height / 2)
            .attr("r", 6)
            .attr("fill", "#007C91")
            .attr("fill-opacity", 0.5);

        // Show highlight line if position is set
        if (selectionContext?.highlightPosition) {
            chart
                .append("line")
                .attr("class", "highlight-line")
                .attr("x1", x(selectionContext.highlightPosition.date))
                .attr("x2", x(selectionContext.highlightPosition.date))
                .attr("y1", 0)
                .attr("y2", height)
                .attr("stroke", "#3b82f6")
                .attr("stroke-width", 1)
                .style("stroke-dasharray", "4,4")
                .style("pointer-events", "none");
        }
    }, [
        dimensions,
        dateRange,
        previewPoints,
        selectionContext?.highlightPosition,
    ]);

    return (
        <div
            ref={containerRef}
            className="flex-1 h-full border-l border-ae-slate-200"
        >
            <svg ref={svgRef} style={{ width: "100%", height: "100%" }} />
        </div>
    );
};

export const TimelineBlock = ({
    title,
    children,
    initiallyOpen,
    previewPoints,
    loading,
    message,
    dataDateRange,
    open,
    onOpenChange,
}: {
    title: string;
    children: ReactNode;
    previewPoints: Date[];
    initiallyOpen: boolean;
    loading: boolean;
    message?: ReactNode | string;
    dataDateRange: {
        start: Date;
        end: Date;
    };
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
}) => {
    const [internalExpanded, setInternalExpanded] = useState(initiallyOpen);
    const expanded = open !== undefined ? open : internalExpanded;

    const handleToggle = () => {
        const newValue = !expanded;
        setInternalExpanded(newValue);
        onOpenChange?.(newValue);
    };

    return (
        <div className="w-full -mt-[1px]">
            <div className="w-full h-8 flex items-center">
                <div className="w-[200px] h-full flex items-center gap-2 border-y p-2">
                    <button
                        onClick={handleToggle}
                        title="Expand section"
                        className="p-1 rounded hover:bg-neutral-200"
                    >
                        <FontAwesomeIcon
                            icon={expanded ? faChevronDown : faChevronRight}
                            className="h-3"
                        />
                    </button>
                    {title}
                    {loading && (
                        <FontAwesomeIcon
                            icon={faSpinner}
                            className="animate-spin"
                        />
                    )}
                </div>
                <div className="flex-1 h-full border-t border-b border-neutral-200 bg-[#EBEBEB] relative">
                    {message && (
                        <div className="absolute bg-[#EBEBEB] text-neutral-600 w-full h-full px-3 flex items-center">
                            {message}
                        </div>
                    )}
                    <PreviewPlot
                        dateRange={dataDateRange}
                        previewPoints={previewPoints}
                    />
                </div>
            </div>
            <div className={expanded ? "block" : "hidden"}>{children}</div>
        </div>
    );
};
